import {
  ChangeDetectionStrategy,
  Component,
  viewChild,
  contentChildren,
  inject,
  signal,
  input,
  effect,
  untracked
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ViewportRuler } from '@angular/cdk/overlay';

import { HorizontalScrollDirection } from '../../domain/direction';
import { ScrollableDirective } from '../scroll/scrollable.directive';
import { IconComponent } from '../icon/icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { SliderItemComponent } from './slider-item.component';

import type {
  AfterViewInit,
  OnDestroy,
  OnInit,
  InputSignal,
  Signal,
  WritableSignal
} from '@angular/core';

@Component({
  selector: 'td-wb-slider',
  templateUrl: 'slider.component.html',
  styleUrls: ['slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, IconComponent, ScrollableDirective, TranslatePipe],
  host: {
    '[class.td-wb-slider--no-scrollbar]': 'noScrollbar()'
  }
})
export class SliderComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly SCROLL_EDGE_THRESHOLD: number = 15;

  tabbable: InputSignal<boolean> = input<boolean>(true);
  ariaLabelledby: InputSignal<string | undefined> = input<string | undefined>();
  removeFade: InputSignal<boolean> = input<boolean>(false);
  noScrollbar: InputSignal<boolean> = input<boolean>(false);
  displaySliderButtons: InputSignal<boolean> = input<boolean>(true);

  scrollable: Signal<ScrollableDirective> = viewChild.required(ScrollableDirective);
  items: Signal<ReadonlyArray<SliderItemComponent>> = contentChildren(SliderItemComponent);

  showNextButton: WritableSignal<boolean> = signal<boolean>(false);
  showPrevButton: WritableSignal<boolean> = signal<boolean>(false);

  private readonly viewportRuler: ViewportRuler = inject(ViewportRuler);
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor() {
    effect(() => {
      this.items();
      untracked(() => this.setScrollHandler());
    });
  }

  ngOnInit(): void {
    this.setScrollHandler();
  }

  ngAfterViewInit(): void {
    this.scrollable().elementScrolled().pipe(takeUntil(this.destroy)).subscribe((): void => this.scrollHandler());
    this.viewportRuler.change().pipe(takeUntil(this.destroy)).subscribe((): void => this.scrollHandler());
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  next(): void {
    this.scroll(HorizontalScrollDirection.RIGHT);
  }

  previous(): void {
    this.scroll(HorizontalScrollDirection.LEFT);
  }

  private setScrollHandler(): void {
    // Schedule button display check after the items list has been rendered to get the correct scroll size
    Promise.resolve().then((): void => {
      if (this.scrollable()) {
        this.scrollHandler();
      }
    });
  }

  private scrollHandler(): void {
    const scrollable = this.scrollable();
    const leftScrollOffset = scrollable.measureScrollOffset('left');
    const rightScrollOffset = scrollable.measureScrollOffset('right');
    const showPrevButton = leftScrollOffset > SliderComponent.SCROLL_EDGE_THRESHOLD;
    const showNextButton = rightScrollOffset > SliderComponent.SCROLL_EDGE_THRESHOLD;

    if (this.showPrevButton() !== showPrevButton || this.showNextButton() !== showNextButton) {
      this.showPrevButton.set(showPrevButton);
      this.showNextButton.set(showNextButton);
    }
  }

  private scroll(direction: HorizontalScrollDirection): void {
    const scrollable = this.scrollable();
    const scrollSize = this.measureScrollable();
    const scrollOffset = scrollable.measureScrollOffset(direction);
    let scrollEndPoint = scrollOffset < scrollSize ? 0 : scrollOffset - scrollSize;

    if (scrollEndPoint <= SliderComponent.SCROLL_EDGE_THRESHOLD) {
      scrollEndPoint = -1; // the tile is always cut off by one pixel
    }
    if (direction === HorizontalScrollDirection.LEFT) {
      scrollable.scrollTo({ left: scrollEndPoint, behavior: 'smooth' });
    } else {
      scrollable.scrollTo({ right: scrollEndPoint, behavior: 'smooth' });
    }
  }

  private measureScrollable(): number {
    return this.scrollable().getElementRef().nativeElement.getBoundingClientRect().width;
  }
}
